import * as Yup from 'yup';

const ONE_MEGABYTE = 1048576;

export function isValidType(file: File): boolean {
  return ['image/jpg', 'image/jpeg', 'image/png'].includes(file.type);
}

export function isValidSize(file: File): boolean {
  if (file.size > ONE_MEGABYTE) {
    return false;
  }
  return true;
}

export const isValidFileName = (fileName: string) => {
  const invalidChars = /[<>:"/\\|?*%]/;
  return !invalidChars.test(fileName);
};

export const isZipFile = (file: File): boolean => {
  return ['application/zip', 'application/x-zip-compressed'].includes(
    file.type
  );
};

export const allowedFileTypes = [
  'application/zip',
  'application/x-zip-compressed',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime',
  'application/pdf',
  'text/plain'
];

export const isValidFileType = (file: File): boolean => {
  return allowedFileTypes.includes(file.type);
};

export function isValidDimension(file: File): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (img.width >= 160 && img.height >= 160) {
        resolve(true);
      } else {
        resolve(false);
      }
    };
    img.onerror = () => {
      reject(new Error('Failed to load the image. Please try again.'));
    };
    img.src = URL.createObjectURL(file);
  });
}

export function isImageTransparent(file: File): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Check for pixels with alpha value less than 255
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 255) {
            resolve(true); // Transparent pixel found
            return;
          }
        }
      }
      resolve(false); // No transparent pixels found
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

interface ValidationResult {
  isValid: boolean;
  formErrors: { [key: string]: string };
}

export const validateForm = async (
  formData: any,
  schema: Yup.ObjectSchema<any>
): Promise<ValidationResult> => {
  try {
    await schema.validate(formData, { abortEarly: false });

    return {
      isValid: true,
      formErrors: {}
    };
  } catch (error: Error | any) {
    const formErrors: { [key: string]: string } = error.inner.reduce(
      (acc: any, e: any) => {
        acc[e.path] = e.message;
        return acc;
      },
      {}
    );
    return {
      isValid: false,
      formErrors
    };
  }
};

interface ValidationRule {
  validate: (file: File) => Promise<boolean> | boolean;
  message: string;
}

export const validateFile = async (
  file: File,
  validationRules: ValidationRule[]
) => {
  for (const rule of validationRules) {
    try {
      const result = await rule.validate(file);
      if (!result) {
        return { isValid: false, message: rule.message };
      }
    } catch (err: any) {
      return {
        isValid: false,
        message: err.message || 'Error during file validation.'
      };
    }
  }
  return { isValid: true, message: '' };
};
